/**
 * Datas do backend no painel.
 *
 * O backend serializa `LocalDateTime` SEM fuso. Há dois tipos de campo:
 * - instantes internos (`statusUpdate`, `createdDate`…): gravados com `LocalDateTime.now()` da JVM
 *   (UTC em produção) → `parseBackendDate` (trata como UTC, regra da gui `parseBackendDate`);
 * - inícios de status exibidos (`sessionStatusBegin`, `phoneStatusBegin`): desde o fix de fuso do
 *   backend (`LogService.updatePhoneStatusJobInt` / `PhoneService.updatePhone` → `appZone()`) são
 *   relógio de parede no fuso da APLICAÇÃO (config `timezone`, padrão America/Sao_Paulo)
 *   → `parseAppDateTime(input, appTz)`. Lê-los como UTC desloca o cronômetro em +3h ("03:00:xx").
 *
 * Em ambos os casos um designador explícito (Z / ±hh:mm) é respeitado.
 */

type WallClock = { y: number; mo: number; d: number; h: number; mi: number; s: number; ms: number };
type Split = { date: Date } | { wall: WallClock } | null;

function splitBackendDate(input: unknown): Split {
  if (input === null || input === undefined) return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : { date: input };

  if (Array.isArray(input)) {
    // Jackson sem JavaTimeModule configurado serializa como [y, M, d, h, m, s, nanos]
    const [y, mo, d, h = 0, mi = 0, s = 0, nanos = 0] = input.map((n) => Number(n));
    if (!y || !mo || !d) return null;
    return { wall: { y, mo: mo - 1, d, h, mi, s, ms: Math.floor(nanos / 1e6) } };
  }

  const raw = String(input).trim();
  if (!raw) return null;

  const normalized = raw.includes("T") || !raw.includes(" ") ? raw : raw.replace(" ", "T");
  const match =
    /^(\d{1,4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/.exec(normalized);

  if (!match) {
    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : { date: fallback };
  }

  if (match[8]) {
    const zDate = new Date(normalized);
    if (!Number.isNaN(zDate.getTime())) return { date: zDate };
  }

  let y = parseInt(match[1], 10);
  if (match[1].length < 4) y += 2000;
  return {
    wall: {
      y,
      mo: parseInt(match[2], 10) - 1,
      d: parseInt(match[3], 10),
      h: parseInt(match[4], 10),
      mi: parseInt(match[5], 10),
      s: match[6] ? parseInt(match[6], 10) : 0,
      ms: match[7] ? parseInt(match[7].slice(1).padEnd(3, "0").slice(0, 3), 10) : 0,
    },
  };
}

function wallAsUtc(w: WallClock): number {
  return Date.UTC(w.y, w.mo, w.d, w.h, w.mi, w.s, w.ms);
}

/**
 * Datas do backend (LocalDateTime sem fuso) tratadas como UTC — mesma regra de
 * `gui/utils/dateUtils.parseBackendDate`. Use para instantes internos (statusUpdate…).
 */
export function parseBackendDate(input: unknown): Date | null {
  const sp = splitBackendDate(input);
  if (!sp) return null;
  if ("date" in sp) return sp.date;
  const utc = new Date(wallAsUtc(sp.wall));
  return Number.isNaN(utc.getTime()) ? null : utc;
}

const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

/** Deslocamento (ms) do fuso `tz` no instante `utcMs` (positivo a leste de UTC). */
function zoneOffsetMs(utcMs: number, tz: string): number {
  let fmt = offsetFormatters.get(tz);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    offsetFormatters.set(tz, fmt);
  }
  const parts = fmt.formatToParts(new Date(utcMs));
  const get = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return asUtc - (utcMs - (((utcMs % 1000) + 1000) % 1000));
}

/** Converte relógio de parede no fuso IANA `tz` para o instante absoluto (trata horário de verão). */
function wallClockInZone(w: WallClock, tz: string): Date | null {
  const guess = wallAsUtc(w);
  if (Number.isNaN(guess)) return null;
  try {
    const off1 = zoneOffsetMs(guess, tz);
    let t = guess - off1;
    const off2 = zoneOffsetMs(t, tz);
    if (off2 !== off1) t = guess - off2;
    return new Date(t);
  } catch {
    return new Date(guess); // fuso inválido: comportamento antigo (UTC)
  }
}

/**
 * Datas de relógio de parede gravadas no fuso da aplicação (config `timezone`):
 * `sessionStatusBegin` / `phoneStatusBegin`. Ver `useAppTimezone`.
 */
export function parseAppDateTime(input: unknown, appTimeZone: string): Date | null {
  const sp = splitBackendDate(input);
  if (!sp) return null;
  if ("date" in sp) return sp.date;
  return wallClockInZone(sp.wall, appTimeZone);
}

const PT_BR_DATETIME: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

/** dd/MM/yyyy HH:mm de um instante já convertido, em pt-BR no fuso do navegador (como a gui). */
export function formatDateTime(date: Date | null | undefined, timeZone?: string): string {
  if (!date) return "";
  try {
    return date.toLocaleString("pt-BR", { ...PT_BR_DATETIME, timeZone });
  } catch {
    return date.toLocaleString("pt-BR", PT_BR_DATETIME);
  }
}

/** dd/MM/yyyy HH:mm em pt-BR no fuso do navegador (como a gui) — entrada tratada como UTC. */
export function formatBackendDateTime(input: unknown, timeZone?: string): string {
  const date = parseBackendDate(input);
  if (!date) return input == null ? "" : String(input);
  return formatDateTime(date, timeZone);
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

/** hh:mm:ss (omitindo horas quando 0) — formato do DigitalClock da gui. */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

/** m:ss — usado em espera de fila (QueueStatusSum / callers). */
export function formatMinSec(totalSeconds: number | null | undefined): string {
  const t = Number(totalSeconds);
  if (!Number.isFinite(t) || t < 0) return "0:00";
  const secs = Math.floor(t) % 60;
  const mins = Math.floor(Math.floor(t) / 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
}

/** Duração em minutos (média de pausa) em texto curto. */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(Number(minutes))) return "—";
  const m = Math.round(Number(minutes));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${pad(m % 60)}min`;
}
